import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/shared/components/ui/tabs';
import { useToast } from '@/shared/hooks';
import { ProjectFormFields, useProjectFormController } from '@/features/projects/public';
import { useInstantiateProjectTemplate } from '../hooks/useInstantiateProjectTemplate';
import { TemplateGalleryTab } from './TemplateGalleryTab';

const BLANK_PROJECT_FORM_ID = 'project-templates-blank-project-form';

interface CreateProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Unifies "Proyecto en blanco" (inline, via the same controller/fields
 * `ProjectFormDialog` uses for editing — see `tasks/current-story.md` Plan
 * Risk #4) with "Desde plantilla" (system presets + tenant templates) in a
 * single entry point.
 */
export function CreateProjectDialog({ open, onOpenChange }: CreateProjectDialogProps) {
  const { t } = useTranslation('projectTemplates');
  const { toast } = useToast();
  const [tab, setTab] = useState<'blank' | 'template'>('template');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>();
  const instantiate = useInstantiateProjectTemplate();

  const blankForm = useProjectFormController({
    open,
    onSuccess: () => onOpenChange(false),
  });

  function handleOpenChange(next: boolean) {
    onOpenChange(next);
    if (!next) setSelectedTemplateId(undefined);
  }

  function handleCreateFromTemplate() {
    if (!selectedTemplateId) return;
    instantiate.mutate(selectedTemplateId, {
      onSuccess: () => {
        toast.success({ title: t('toast.instantiated') });
        handleOpenChange(false);
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="w-full h-full max-w-none rounded-none overflow-y-auto md:h-[85vh] md:max-w-4xl lg:max-w-5xl md:rounded-lg flex flex-col"
        data-testid="project-templates-create-dialog"
      >
        <DialogHeader>
          <DialogTitle>{t('createDialog.title')}</DialogTitle>
          <DialogDescription>{t('createDialog.description')}</DialogDescription>
        </DialogHeader>

        <Tabs
          value={tab}
          onValueChange={(next) => setTab(next as 'blank' | 'template')}
          className="flex flex-1 flex-col overflow-hidden"
        >
          <TabsList className="self-start">
            <TabsTrigger value="blank" data-testid="project-templates-tab-blank">
              {t('createDialog.tabs.blank')}
            </TabsTrigger>
            <TabsTrigger value="template" data-testid="project-templates-tab-gallery">
              {t('createDialog.tabs.template')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="blank" className="flex-1 overflow-y-auto">
            <form
              id={BLANK_PROJECT_FORM_ID}
              onSubmit={blankForm.onSubmit}
              className="flex flex-col gap-5 py-4"
            >
              <ProjectFormFields
                control={blankForm.control}
                setValue={blankForm.setValue}
                selectedColor={blankForm.selectedColor}
                isPending={blankForm.isPending}
              />
            </form>
          </TabsContent>

          <TabsContent value="template" className="flex-1 overflow-hidden">
            <TemplateGalleryTab
              selectedTemplateId={selectedTemplateId}
              onSelect={setSelectedTemplateId}
            />
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
            {t('createDialog.footer.cancel')}
          </Button>
          {tab === 'blank' ? (
            <Button
              type="submit"
              form={BLANK_PROJECT_FORM_ID}
              disabled={blankForm.isPending}
              data-testid="project-templates-create-blank-button"
            >
              {blankForm.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('createDialog.footer.create')}
            </Button>
          ) : (
            <Button
              type="button"
              disabled={!selectedTemplateId || instantiate.isPending}
              onClick={handleCreateFromTemplate}
              data-testid="project-templates-instantiate-button"
            >
              {instantiate.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('createDialog.footer.create')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
